import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Building2,
  Trophy,
  UserPlus,
  Users,
  Rocket,
  X,
  Plus,
  Upload as UploadIcon,
  PartyPopper,
  ClipboardList,
  Dumbbell,
  Layout as LayoutIcon,
  MessageCircle,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const CATEGORIES = [
  "Piccoli Amici",
  "Primi Calci",
  "Pulcini",
  "Esordienti",
  "Giovanissimi",
  "Allievi",
  "Juniores",
  "Prima Squadra",
  "Under 8",
  "Under 10",
  "Under 12",
  "Under 14",
  "Under 16",
  "Under 18",
];

const STEPS = [
  { key: "club", title: "Dati Società", icon: Building2, required: true },
  { key: "team", title: "Prima Squadra", icon: Trophy, required: true },
  { key: "staff", title: "Invita Staff", icon: UserPlus, required: false },
  { key: "players", title: "Importa Giocatori", icon: Users, required: false },
  { key: "tour", title: "Tour rapido", icon: Rocket, required: false },
];

const TOUR_HIGHLIGHTS = [
  {
    icon: ClipboardList,
    title: "Convocazioni & Distinta Gara",
    text: "Crea convocazioni in 30 secondi e stampa la Distinta FIGC ufficiale. Condividila in chat o WhatsApp.",
  },
  {
    icon: Dumbbell,
    title: "Allenamenti & Libreria esercizi",
    text: "Progetta sedute con drill designer visuale, salva schemi e riutilizza la libreria.",
  },
  {
    icon: LayoutIcon,
    title: "Lavagna tattica multi-frame",
    text: "Anima schemi con timeline ed esporta GIF per il pre-partita.",
  },
  {
    icon: MessageCircle,
    title: "Chat famiglie + Modulistica",
    text: "Comunica con i genitori in tempo reale, gestisci iscrizioni online con firma OTP.",
  },
];

export default function OnboardingWizard({ open, onOpenChange, onComplete, defaultSeason = "2025/2026", seasons = [] }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step 1 — Club
  const [club, setClub] = useState({
    legal_name: "",
    logo_url: null,
    fiscal_code: "",
    vat_number: "",
    address: "",
    phone: "",
    email: "",
    figc_affiliation_code: "",
    president_name: "",
  });

  // Step 2 — Team
  const [team, setTeam] = useState({
    name: "",
    category: "Pulcini",
    season: defaultSeason,
    color: "#16a34a",
  });
  const [createdTeamId, setCreatedTeamId] = useState(null);

  // Step 3 — Staff invites: list of { email, role }
  const [staffInvites, setStaffInvites] = useState([{ email: "", role: "director" }]);

  // Step 4 — Quick players add (textarea-style)
  const [playersText, setPlayersText] = useState("");

  // Reset wizard each time it opens fresh
  useEffect(() => {
    if (!open) return;
    setStepIdx(0);
    setSubmitting(false);
  }, [open]);

  const currentStep = STEPS[stepIdx];

  const goNext = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const goPrev = () => setStepIdx((i) => Math.max(0, i - 1));

  // ---- Logo upload (optional) ----
  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File troppo grande (max 2 MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setClub((c) => ({ ...c, logo_url: data.url }));
      toast.success("Logo caricato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    }
  };

  // ---- Step 1+2 commit: create team with merged club+team data ----
  const commitTeam = async () => {
    if (!team.name.trim()) {
      toast.error("Inserisci il nome della squadra");
      return false;
    }
    if (!team.category.trim()) {
      toast.error("Inserisci la categoria");
      return false;
    }
    setSubmitting(true);
    try {
      const payload = {
        // team-specific
        name: team.name.trim(),
        category: team.category.trim(),
        season: team.season,
        color: team.color,
        // legal/club fields (shared at team level in this schema)
        legal_name: club.legal_name.trim() || null,
        logo_url: club.logo_url || null,
        fiscal_code: club.fiscal_code.trim() || null,
        vat_number: club.vat_number.trim() || null,
        address: club.address.trim() || null,
        phone: club.phone.trim() || null,
        email: club.email.trim() || null,
        figc_affiliation_code: club.figc_affiliation_code.trim() || null,
        president_name: club.president_name.trim() || null,
      };
      const { data } = await api.post("/teams", payload);
      setCreatedTeamId(data.id);
      toast.success("Squadra creata");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione squadra");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 3 commit: send staff invites ----
  const commitInvites = async () => {
    if (!createdTeamId) return true; // shouldn't happen
    const valid = staffInvites.filter((i) => i.email.trim() && /\S+@\S+\.\S+/.test(i.email.trim()));
    if (valid.length === 0) return true; // nothing to send
    setSubmitting(true);
    try {
      let ok = 0;
      for (const inv of valid) {
        try {
          await api.post(`/teams/${createdTeamId}/invites`, {
            role: inv.role,
            email: inv.email.trim().toLowerCase(),
          });
          ok += 1;
        } catch (_) {
          /* skip individual failures */
        }
      }
      if (ok > 0) toast.success(`${ok} invito${ok === 1 ? "" : "i"} creat${ok === 1 ? "o" : "i"}`);
      return true;
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Step 4 commit: quick add players ----
  const commitPlayers = async () => {
    if (!createdTeamId) return true;
    const lines = playersText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length === 0) return true;
    setSubmitting(true);
    try {
      let ok = 0;
      for (const line of lines) {
        // Format: "First Last [, jersey [, role]]"
        const parts = line.split(",").map((p) => p.trim());
        const namePart = parts[0] || "";
        const nameTokens = namePart.split(/\s+/);
        const first_name = nameTokens[0] || "Nome";
        const last_name = nameTokens.slice(1).join(" ") || "Cognome";
        const jersey_number = parts[1] ? Number(parts[1]) || null : null;
        const role = parts[2] || null;
        try {
          await api.post("/players", {
            team_id: createdTeamId,
            first_name,
            last_name,
            jersey_number,
            role,
          });
          ok += 1;
        } catch (_) {
          /* skip */
        }
      }
      if (ok > 0) toast.success(`${ok} giocator${ok === 1 ? "e" : "i"} aggiunt${ok === 1 ? "o" : "i"}`);
      return true;
    } finally {
      setSubmitting(false);
    }
  };

  const markCompleteAndClose = async () => {
    try {
      await api.post("/me/onboarding/complete");
    } catch (_) {
      /* best-effort */
    }
    onComplete?.(createdTeamId);
    onOpenChange(false);
  };

  // ---- Handle "Avanti" per step ----
  const handleNext = async () => {
    if (stepIdx === 0) {
      // Step 1: no API call yet
      goNext();
      return;
    }
    if (stepIdx === 1) {
      // Step 2 commits team creation
      if (!createdTeamId) {
        const ok = await commitTeam();
        if (!ok) return;
      }
      goNext();
      return;
    }
    if (stepIdx === 2) {
      await commitInvites();
      goNext();
      return;
    }
    if (stepIdx === 3) {
      await commitPlayers();
      goNext();
      return;
    }
    if (stepIdx === 4) {
      // Last step → finish
      await markCompleteAndClose();
    }
  };

  const handleSkipStep = async () => {
    if (currentStep.required) return;
    if (stepIdx === STEPS.length - 1) {
      await markCompleteAndClose();
      return;
    }
    goNext();
  };

  const handleSkipAll = async () => {
    if (createdTeamId == null && stepIdx > 1) {
      // edge case — shouldn't happen
    }
    await markCompleteAndClose();
  };

  const stepDots = useMemo(
    () =>
      STEPS.map((s, i) => ({
        ...s,
        done: i < stepIdx,
        active: i === stepIdx,
      })),
    [stepIdx]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden gap-0 border-0 shadow-2xl"
        data-testid="onboarding-wizard"
      >
        <DialogTitle className="sr-only">Setup guidato Calcio Club</DialogTitle>
        <DialogDescription className="sr-only">
          Configura la tua società, la prima squadra e invita lo staff in pochi minuti.
        </DialogDescription>

        {/* Header */}
        <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-sky-700 text-white px-6 py-5 relative">
          <button
            onClick={handleSkipAll}
            className="absolute top-3 right-3 text-xs text-white/80 hover:text-white underline-offset-2 hover:underline flex items-center gap-1"
            data-testid="onboarding-skip-all"
          >
            <X size={12} /> Salta tutto
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-emerald-100" />
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-100">
              Setup guidato
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold leading-tight">
            Benvenuto nel tuo spogliatoio digitale
          </h2>
          <p className="text-sm text-white/80 mt-1">
            Bastano 2 minuti per partire. Puoi saltare ogni passaggio non obbligatorio.
          </p>
          {/* Stepper */}
          <div className="mt-4 flex items-center gap-1.5">
            {stepDots.map((s, i) => (
              <div
                key={s.key}
                className="flex items-center gap-1.5 flex-1"
                data-testid={`onboarding-step-dot-${i}`}
              >
                <div
                  className={cn(
                    "flex-1 h-1.5 rounded-full transition",
                    s.done
                      ? "bg-emerald-300"
                      : s.active
                      ? "bg-white"
                      : "bg-white/20"
                  )}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-white/70">
            <span>
              Passo {stepIdx + 1} di {STEPS.length} ·{" "}
              <span className="text-white font-medium">{currentStep.title}</span>
            </span>
            {!currentStep.required && (
              <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/90 text-[10px]">
                Opzionale
              </span>
            )}
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto bg-slate-50">
          {/* STEP 1 — CLUB */}
          {stepIdx === 0 && (
            <div className="space-y-4" data-testid="onboarding-step-club">
              <div className="text-sm text-slate-600">
                Inserisci i dati della tua società. Tutti i campi sono <strong>opzionali</strong>:
                potrai aggiornarli quando vuoi dalle impostazioni della squadra.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Logo società</Label>
                  <div className="flex items-center gap-3 mt-1.5">
                    {club.logo_url ? (
                      <img
                        src={club.logo_url}
                        alt="Logo"
                        className="h-14 w-14 rounded-xl object-cover bg-white border border-slate-200"
                      />
                    ) : (
                      <div className="h-14 w-14 rounded-xl bg-emerald-50 border border-dashed border-emerald-300 flex items-center justify-center">
                        <Building2 size={20} className="text-emerald-600" />
                      </div>
                    )}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => handleLogoUpload(e.target.files?.[0])}
                        data-testid="onboarding-logo-input"
                      />
                      <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700">
                        <UploadIcon size={14} /> Carica logo (max 2MB)
                      </span>
                    </label>
                    {club.logo_url && (
                      <button
                        type="button"
                        onClick={() => setClub((c) => ({ ...c, logo_url: null }))}
                        className="text-xs text-slate-500 hover:text-red-600"
                      >
                        Rimuovi
                      </button>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <Label>Denominazione società</Label>
                  <Input
                    value={club.legal_name}
                    onChange={(e) => setClub({ ...club, legal_name: e.target.value })}
                    placeholder="es. ASD Calcio Esempio"
                    data-testid="onboarding-club-name"
                  />
                </div>
                <div>
                  <Label>Codice Fiscale</Label>
                  <Input
                    value={club.fiscal_code}
                    onChange={(e) => setClub({ ...club, fiscal_code: e.target.value })}
                    placeholder="ASD codice fiscale"
                  />
                </div>
                <div>
                  <Label>Partita IVA</Label>
                  <Input
                    value={club.vat_number}
                    onChange={(e) => setClub({ ...club, vat_number: e.target.value })}
                    placeholder="se presente"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Sede legale</Label>
                  <Input
                    value={club.address}
                    onChange={(e) => setClub({ ...club, address: e.target.value })}
                    placeholder="Via, città, CAP"
                  />
                </div>
                <div>
                  <Label>Telefono</Label>
                  <Input
                    value={club.phone}
                    onChange={(e) => setClub({ ...club, phone: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Email società</Label>
                  <Input
                    type="email"
                    value={club.email}
                    onChange={(e) => setClub({ ...club, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Matricola FIGC</Label>
                  <Input
                    value={club.figc_affiliation_code}
                    onChange={(e) =>
                      setClub({ ...club, figc_affiliation_code: e.target.value })
                    }
                    placeholder="es. 123456"
                  />
                </div>
                <div>
                  <Label>Nome Presidente</Label>
                  <Input
                    value={club.president_name}
                    onChange={(e) => setClub({ ...club, president_name: e.target.value })}
                    placeholder="Per firma documenti"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — TEAM */}
          {stepIdx === 1 && (
            <div className="space-y-4" data-testid="onboarding-step-team">
              <div className="text-sm text-slate-600">
                Crea la tua prima squadra. Potrai aggiungerne altre in qualsiasi momento.
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label>Nome squadra *</Label>
                  <Input
                    value={team.name}
                    onChange={(e) => setTeam({ ...team, name: e.target.value })}
                    placeholder="es. Pulcini 2014"
                    data-testid="onboarding-team-name"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Categoria FIGC *</Label>
                  <Select
                    value={team.category}
                    onValueChange={(v) => setTeam({ ...team, category: v })}
                  >
                    <SelectTrigger data-testid="onboarding-team-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Stagione *</Label>
                  <Select
                    value={team.season}
                    onValueChange={(v) => setTeam({ ...team, season: v })}
                  >
                    <SelectTrigger data-testid="onboarding-team-season">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(seasons.length ? seasons : [defaultSeason]).map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Colore sociale</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input
                      type="color"
                      value={team.color}
                      onChange={(e) => setTeam({ ...team, color: e.target.value })}
                      className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer"
                      data-testid="onboarding-team-color"
                    />
                    <div
                      className="h-10 flex-1 rounded-lg flex items-center px-3 text-sm text-white font-medium"
                      style={{ background: team.color }}
                    >
                      {team.color}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — STAFF */}
          {stepIdx === 2 && (
            <div className="space-y-4" data-testid="onboarding-step-staff">
              <div className="text-sm text-slate-600">
                Invita i tuoi <strong>dirigenti</strong> o <strong>collaboratori</strong> via email.
                Riceveranno un link di invito. Puoi farlo anche dopo.
              </div>
              <div className="space-y-2">
                {staffInvites.map((inv, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`onboarding-invite-row-${i}`}>
                    <Input
                      type="email"
                      placeholder="email@esempio.it"
                      value={inv.email}
                      onChange={(e) => {
                        const next = [...staffInvites];
                        next[i] = { ...inv, email: e.target.value };
                        setStaffInvites(next);
                      }}
                      className="flex-1"
                    />
                    <Select
                      value={inv.role}
                      onValueChange={(v) => {
                        const next = [...staffInvites];
                        next[i] = { ...inv, role: v };
                        setStaffInvites(next);
                      }}
                    >
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="director">Dirigente</SelectItem>
                      </SelectContent>
                    </Select>
                    {staffInvites.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() =>
                          setStaffInvites(staffInvites.filter((_, idx) => idx !== i))
                        }
                      >
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setStaffInvites([...staffInvites, { email: "", role: "director" }])
                  }
                  data-testid="onboarding-add-invite"
                >
                  <Plus size={14} className="mr-1" /> Aggiungi altro invito
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Suggerimento: i genitori potranno essere invitati dalla pagina Anagrafica
                Giocatori, dove collegherai il figlio al genitore.
              </p>
            </div>
          )}

          {/* STEP 4 — PLAYERS */}
          {stepIdx === 3 && (
            <div className="space-y-4" data-testid="onboarding-step-players">
              <div className="text-sm text-slate-600">
                Aggiungi velocemente i tuoi giocatori. Un nome per riga:
                <br />
                <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                  Mario Rossi, 10, Attaccante
                </code>{" "}
                (numero maglia e ruolo opzionali).
              </div>
              <textarea
                value={playersText}
                onChange={(e) => setPlayersText(e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 font-mono text-sm bg-white"
                placeholder={"Mario Rossi, 10, Attaccante\nLuca Bianchi, 7, Centrocampista\nGiulia Verdi, 1, Portiere"}
                data-testid="onboarding-players-textarea"
              />
              <p className="text-xs text-slate-500">
                Foto, contatti genitori, tessera FIGC e dati medici potrai aggiungerli dopo
                dall&apos;Anagrafica.
              </p>
            </div>
          )}

          {/* STEP 5 — TOUR */}
          {stepIdx === 4 && (
            <div className="space-y-4" data-testid="onboarding-step-tour">
              <div className="flex items-center gap-2 text-emerald-700">
                <PartyPopper size={18} />
                <span className="font-medium">Sei pronto!</span>
              </div>
              <p className="text-sm text-slate-600">
                Ecco le 4 funzioni che useranno di più i Mister come te:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TOUR_HIGHLIGHTS.map((h) => (
                  <div
                    key={h.title}
                    className="p-4 rounded-xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition"
                  >
                    <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center mb-2">
                      <h.icon size={18} />
                    </div>
                    <div className="font-medium text-sm leading-tight">{h.title}</div>
                    <div className="text-xs text-slate-500 mt-1 leading-relaxed">{h.text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-white border-t border-slate-200 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={goPrev}
            disabled={stepIdx === 0 || submitting}
            data-testid="onboarding-prev"
          >
            <ChevronLeft size={16} className="mr-1" /> Indietro
          </Button>
          <div className="flex items-center gap-2">
            {!currentStep.required && stepIdx < STEPS.length - 1 && (
              <Button
                variant="ghost"
                onClick={handleSkipStep}
                disabled={submitting}
                data-testid="onboarding-skip-step"
                className="text-slate-500"
              >
                Salta
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="onboarding-next"
            >
              {stepIdx === STEPS.length - 1 ? (
                <>
                  <Check size={16} className="mr-1" /> Inizia
                </>
              ) : (
                <>
                  Avanti <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
